import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Clock, Target, Play, RefreshCw, CheckCircle, Brain } from 'lucide-react';
import { getSafeToken } from '../../../../../utils/tokenHelper';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
    ? _eplConsole
    : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

/**
 * 🎲 BMLSimulatePhase - Generador de datos simulados del experimento
 * 
 * Propósito:
 * - Llenar el gap entre MEASURE y LEARN
 * - Generar datos realistas basados en las métricas definidas
 * - Proporcionar contexto para la reflexión en LEARN
 * - Mantener la experiencia educativa completa
 */

const BMLSimulateStep = ({ 
    onSimulationComplete, 
    measureData = null, 
    isActive = false,
    initialData = null 
}) => {
    const [simulationStep, setSimulationStep] = useState('setup'); // 'setup', 'running', 'completed'
    const [simulatedData, setSimulatedData] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [experimentDuration, setExperimentDuration] = useState(7); // días

    // 🔍 DEBUG: Logging al montar el componente
    useEffect(() => {
        console.log('[🔍 SIMULATE MOUNT] measureData:', measureData);
        console.log('[🔍 SIMULATE MOUNT] initialData:', initialData);
        console.log('[🔍 SIMULATE MOUNT] isActive:', isActive);
    }, [measureData, initialData, isActive]);

    // 🧠 Llamada a IA Analista según especificación del experto
    const generateSimulatedResults = async () => {
        // 🔍 DEBUG: Mostrar datos completos recibidos
        console.log('[🔍 DEBUG SIMULATE] measureData completo:', measureData);
        console.log('[🔍 DEBUG SIMULATE] initialData completo:', initialData);
        
        // � VERIFICACIÓN CRÍTICA: measureData debe existir
        if (!measureData) {
            console.error('[❌ SIMULATE] measureData es null/undefined al momento de generar resultados');
            console.error('[❌ SIMULATE] Esto indica un problema de timing o prop passing');
            return null;
        }
        
        // �🔍 Mapear datos de MEASURE correctamente
        const rawMetrics = measureData?.metricas_clave || measureData?.kpis_principales || [];
        const metrics = rawMetrics.filter(metric => metric && metric.toString().trim() !== '');
        
        console.log('[🔍 DEBUG SIMULATE] rawMetrics:', rawMetrics);
        console.log('[🔍 DEBUG SIMULATE] metrics filtradas:', metrics);
        
        if (!metrics || metrics.length === 0) {
            console.warn('[🧠 AI ANALYST] No hay métricas válidas después del filtrado. rawMetrics:', rawMetrics);
            return null;
        }
        
        console.log('[🧠 AI ANALYST] Métricas filtradas:', metrics);

        // 📋 Construir contrato de entrada según el prompt del experto
        const aiRequest = {
            lab_id: "lab1",
            session_id: `session_${Date.now()}`,
            hypothesis: {
                id: `hyp_lab1_${Date.now()}`,
                text: measureData.hipotesis_validar || measureData.hypothesis || "Hipótesis no definida"
            },
            measure_plan: {
                metrics: metrics.map(metric => ({
                    name: metric,
                    target: ">=5%", // TODO: Extraer targets reales del plan MEASURE
                    tool: "GA4" // TODO: Mapear herramientas reales
                })),
                horizon_days: experimentDuration,
                expected_universe: Math.floor(Math.random() * 200) + 50
            },
            context: {
                domain: "practicas_universitarias",
                locale: "es-PE",
                seed: Date.now()
            }
        };

        try {
            // 🚀 Llamada real a la IA Analista (placeholder por ahora)
            console.log('[🧠 AI ANALYST] Solicitando análisis avanzado:', aiRequest);
            console.log('[🧠 AI ANALYST] Métricas recibidas:', metrics);
            
            // 🌐 Llamada real al endpoint consolidado
            const token = getSafeToken();
            const response = await fetch('/api/asistentes/innovatech/simulate', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : undefined
                },
                body: JSON.stringify(aiRequest)
            });
            
            if (!response.ok) {
                throw new Error(`API Error: ${response.status}`);
            }

            const result = await response.json();
            console.log('[🧠 AI ANALYST] Respuesta del servidor:', result);
            
            if (result.success && result.simulation_data) {
                console.log('[🧠 AI ANALYST] Simulación exitosa:', result.simulation_data);
                return result.simulation_data;
            } else {
                throw new Error(result.error || 'Error en simulación');
            }

        } catch (error) {
            console.error('[🧠 AI ANALYST] Error en simulación avanzada:', error);
            console.warn('[🧠 AI ANALYST] Usando fallback básico para métricas:', metrics);
            
            // 🔄 Fallback a generación local básica
            try {
                return generateBasicFallbackResults(metrics);
            } catch (fallbackError) {
                console.error('[🧠 AI ANALYST] Error en fallback:', fallbackError);
                // Último recurso: datos mínimos
                return {
                    universe: { n_users: 50, period_days: 7 },
                    kpis: [{ name: "CTR", value: 5.2, unit: "%", confidence: 0.8, trend: "up" }],
                    analysis: {
                        descriptive: "Simulación básica completada",
                        diagnostic: "Error en generación de datos - modo de emergencia",
                        recommendation: "Revisar configuración del sistema"
                    },
                    veredicto: "revisar"
                };
            }
        }
    };

    // 🎯 Generar resultados avanzados según schema del experto
    const generateAdvancedMockResults = (aiRequest) => {
        const metrics = aiRequest.measure_plan.metrics;
        const nUsers = aiRequest.measure_plan.expected_universe;
        
        // 🔧 Normalizar métricas (pueden venir como strings o como objects)
        const normalizedMetrics = metrics.map(metric => {
            if (typeof metric === 'string') {
                return { name: metric, target: ">=5%" };
            }
            return { name: metric.name || metric, target: metric.target || ">=5%" };
        });
        
        console.log('[🧠 MOCK] Métricas normalizadas:', normalizedMetrics);
        
        // 🏗️ Estructura v2.3 según especificaciones del experto
        return {
            tipo_visual: "simulacion_resultados",
            mostrar_input_text: false,
            lab_id: aiRequest.lab_id,
            step_id: "bml_measure_resultados", 
            hypothesis_id: aiRequest.hypothesis.id,
            
            // � Hipótesis evaluada (NUEVO v2.3)
            hypothesis_text: aiRequest.hypothesis.text,
            
            // �👥 Universo simulado realista
            universe: {
                n_users: nUsers + Math.floor(Math.random() * 50),
                period_days: aiRequest.measure_plan.horizon_days,
                age_avg: 22,
                gender_ratio: { male: 0.58, female: 0.42 },
                segments: ["Sistemas", "Industrial", "Electrónica", "Data Science"],
                location: "Lima Metropolitana"
            },
            
            // 📊 Comparativo y benchmarks (NUEVO v2.3)
            comparativo: {
                fuente_referencia: "industria",
                editable_por_usuario: true,
                benchmark: normalizedMetrics.reduce((acc, metric) => {
                    // Generar benchmarks realistas por tipo de KPI
                    const { value } = generateRealisticKPI(metric.name, metric.target);
                    acc[metric.name] = Math.floor(value * 0.8 * 100) / 100; // 20% menor como benchmark
                    return acc;
                }, {}),
                nota: "Los benchmarks pueden ser editados por el alumno; se registra auditoría."
            },
            
            // 📊 KPIs con estructura v2.3
            kpis: normalizedMetrics.map((metric, index) => {
                const { value, unit, confidence } = generateRealisticKPI(metric.name, metric.target);
                return {
                    name: metric.name,
                    value: value,
                    unit: unit,
                    confidence: confidence
                };
            }),
            
            // 🎯 Interpretación IA por KPI (NUEVO v2.3)
            interpretacion_kpi: normalizedMetrics.map((metric, index) => {
                const { value, unit } = generateRealisticKPI(metric.name, metric.target);
                const benchmark = Math.floor(value * 0.8 * 100) / 100;
                const isAbove = value > benchmark;
                const difference = Math.abs(value - benchmark);
                
                // Lógica de etiquetas según especificaciones
                let etiqueta, tendencia, comparacion;
                
                if (difference / benchmark > 0.2) {
                    // Diferencia significativa (>20%)
                    if (isAbove && (metric.name.toLowerCase().includes('ctr') || metric.name.toLowerCase().includes('satisfaccion'))) {
                        etiqueta = "validada";
                        tendencia = "positiva";
                        comparacion = "supera estándar";
                    } else if (isAbove && metric.name.toLowerCase().includes('tiempo')) {
                        etiqueta = "revisar";
                        tendencia = "negativa"; 
                        comparacion = "por encima del ideal";
                    } else {
                        etiqueta = "ambigua";
                        tendencia = "neutra";
                        comparacion = "desviación notable";
                    }
                } else {
                    // Diferencia menor
                    etiqueta = "ambigua";
                    tendencia = "neutra";
                    comparacion = "similar al estándar";
                }
                
                // Generar interpretación de confianza
                const { confidence } = generateRealisticKPI(metric.name, metric.target);
                const confidenceInfo = interpretConfidence(confidence);
                
                return {
                    name: metric.name,
                    comparacion: comparacion,
                    tendencia: tendencia,
                    etiqueta: etiqueta,
                    explicacion: `${value}${unit} ${comparacion === 'supera estándar' ? '>' : comparacion === 'por encima del ideal' ? '>' : '≈'} ${benchmark}${unit} → ${
                        etiqueta === 'validada' ? 'rendimiento sólido' :
                        etiqueta === 'revisar' ? 'requiere optimización' :
                        'resultado moderado'
                    }.`,
                    // 🧠 Nuevos campos para interpretación de confianza (según documento experto)
                    nivel_confianza: confidenceInfo.nivel,
                    interpretacion_ia: `El resultado está ${comparacion}. ${confidenceInfo.explicacion}`
                };
            }),
            
            // 🔍 Segmentación por carrera
            segments_breakdown: [
                { segment: "Sistemas", kpis: [{ name: normalizedMetrics[0]?.name || "CTR", value: 7.2, unit: "%" }] },
                { segment: "Industrial", kpis: [{ name: normalizedMetrics[0]?.name || "CTR", value: 5.4, unit: "%" }] },
                { segment: "Electrónica", kpis: [{ name: normalizedMetrics[0]?.name || "CTR", value: 6.8, unit: "%" }] }
            ],
            
            // 💬 Feedback cualitativo (v2.3 con "tone")
            qual_feedback: [
                { segment: "Sistemas", quote: "Vi los filtros, pero no entendí su propósito.", tone: "neutral" },
                { segment: "Industrial", quote: "El filtro no cambiaba mucho la lista.", tone: "negative" },
                { segment: "Electrónica", quote: "La idea es buena, pero necesita mejoras.", tone: "positive" },
                { segment: "Data Science", quote: "Funciona básico, mejoraría la UX.", tone: "neutral" }
            ],
            
            // 💬 Feedback cualitativo para UI (formato esperado)
            feedback_cualitativo: [
                { quote: "Vi los filtros, pero no entendí su propósito.", sentiment: "neutral" },
                { quote: "El filtro no cambiaba mucho la lista.", sentiment: "negative" },
                { quote: "El orden de opciones me confundió.", sentiment: "negative" },
                { quote: "Me gusta la idea pero necesita mejoras.", sentiment: "positive" }
            ],
            
            // 🧠 Análisis interpretativo IA (3 capas) - v2.3 con confianza
            analysis: (() => {
                const avgConfidence = normalizedMetrics.reduce((sum, metric) => {
                    const { confidence } = generateRealisticKPI(metric.name, metric.target);
                    return sum + confidence;
                }, 0) / normalizedMetrics.length;
                
                const confidenceInfo = interpretConfidence(avgConfidence);
                const finalUsers = nUsers + Math.floor(Math.random() * 50);
                
                return {
                    descriptive: `Se evaluó el MVP en ${aiRequest.measure_plan.horizon_days} días con ${finalUsers} usuarios. Confianza general: ${Math.round(avgConfidence * 100)}% (${confidenceInfo.nivel}).`,
                    diagnostic: `Buen interés inicial; fricción cognitiva en uso del filtrado. Los resultados tienen ${confidenceInfo.nivel} confianza estadística.`,
                    recommendation: `${confidenceInfo.accion} Agregar tutorial guiado y renombrar etiquetas antes de pivotar segmento.`
                };
            })(),
            
            // 📈 Visualización adaptativa
            visualizacion: {
                tipo: "bar", // TODO: Lógica según visual_rules del prompt
                titulo: "Indicadores clave del experimento",
                indicadores: normalizedMetrics.map(m => {
                    const { value, unit } = generateRealisticKPI(m.name, m.target);
                    return { nombre: m.name, valor: value, unidad: unit };
                })
            },
            
            // 🎯 Dashboard de veredicto (v2.3)
            summary_score: {
                impact: "medio",
                clarity: "alto", 
                retention: "medio"
            },
            
            // 📊 Veredicto automático basado en reglas v2.3
            veredicto: (() => {
                // Regla: aprobado si ≥70% de KPIs están "validada"
                const validatedCount = normalizedMetrics.filter((_, index) => {
                    const { value } = generateRealisticKPI(normalizedMetrics[index].name, normalizedMetrics[index].target);
                    const benchmark = Math.floor(value * 0.8 * 100) / 100;
                    return value > benchmark * 1.2; // >20% mejor que benchmark = validada
                }).length;
                
                const validationRate = validatedCount / normalizedMetrics.length;
                return validationRate >= 0.7 ? "aprobado" : "revisar";
            })(),
            
            confidence: 0.84,
            resumen_analitico: "Interacción sólida vs. estándar; revisar fricción de uso para mejorar satisfacción y retención.",
            immutable: true,
            next_step: "bml_learn"
        };
    };

    // 🧠 Interpretar nivel de confianza según especificaciones del experto
    const interpretConfidence = (confidence) => {
        const confidencePercent = Math.round(confidence * 100);
        
        if (confidencePercent > 85) {
            return {
                nivel: "alta",
                color: "green",
                icon: "✅",
                bgColor: "bg-green-100",
                textColor: "text-green-700",
                descripcion: "Confianza alta. Resultados consistentes y representativos.",
                accion: "Aprendizaje sólido, puede avanzar a fase LEARN.",
                explicacion: `Con ${confidencePercent}%, este resultado es muy fiable. Los datos son consistentes y puedes tomar decisiones con seguridad.`
            };
        } else if (confidencePercent >= 70) {
            return {
                nivel: "media",
                color: "yellow", 
                icon: "⚠️",
                bgColor: "bg-yellow-100",
                textColor: "text-yellow-700",
                descripcion: "Confianza media. Resultado orientativo, requiere más datos.",
                accion: "Repetir o ampliar muestra.",
                explicacion: `Con ${confidencePercent}%, el resultado es orientativo, pero podría variar si repites el experimento con más usuarios.`
            };
        } else {
            return {
                nivel: "baja",
                color: "red",
                icon: "❗",
                bgColor: "bg-red-100", 
                textColor: "text-red-700",
                descripcion: "Confianza baja. Muestra insuficiente o resultados dispersos.",
                accion: "No tomar decisiones aún, rediseñar experimento.",
                explicacion: `Con ${confidencePercent}%, la muestra es insuficiente. Los resultados pueden ser muy variables, necesitas más datos.`
            };
        }
    };

    // 🎯 Generar KPI realista según reglas del prompt
    const generateRealisticKPI = (metricName, target) => {
        const metricLower = metricName.toLowerCase();
        let value, unit, confidence;
        
        // 📊 Aplicar visual_rules del prompt
        if (metricLower.includes('ctr') || metricLower.includes('click') || metricLower.includes('conversion')) {
            value = parseFloat((Math.random() * 8 + 2).toFixed(1)); // 2-10%
            unit = '%';
            confidence = 0.85 + Math.random() * 0.1;
        } else if (metricLower.includes('satisfaccion') || metricLower.includes('nps')) {
            value = parseFloat((Math.random() * 3 + 2).toFixed(1)); // 2-5 /5
            unit = '/5';
            confidence = 0.8 + Math.random() * 0.15;
        } else if (metricLower.includes('tiempo') || metricLower.includes('duracion')) {
            value = Math.floor(Math.random() * 200 + 60); // 60-260 seg
            unit = 'seg';
            confidence = 0.75 + Math.random() * 0.15;
        } else if (metricLower.includes('usuarios') || metricLower.includes('recurrentes')) {
            value = Math.floor(Math.random() * 40 + 10); // 10-50%
            unit = '%';
            confidence = 0.8 + Math.random() * 0.1;
        } else {
            // Métrica genérica
            value = parseFloat((Math.random() * 80 + 20).toFixed(1));
            unit = 'puntos';
            confidence = 0.7 + Math.random() * 0.2;
        }
        
        return { value, unit, confidence: parseFloat(confidence.toFixed(2)) };
    };

    // 🔄 Fallback básico para casos de error (compatible con UI)
    const generateBasicFallbackResults = (metricsInput) => {
        const metricNames = Array.isArray(metricsInput) 
            ? metricsInput.map(m => typeof m === 'string' ? m : m.name)
            : ['Métrica general'];
            
        return {
            // ✅ Estructura compatible con generateAdvancedMockResults
            tipo_visual: "simulacion_resultados",
            lab_id: "lab1",
            universe: { 
                n_users: Math.floor(Math.random() * 100) + 50, 
                period_days: 7,
                status: "simulacion_local"
            },
            
            kpis: metricNames.map(metricName => {
                const { value, unit, confidence } = generateRealisticKPI(metricName, ">=5%");
                return { 
                    name: metricName, 
                    value, 
                    unit, 
                    confidence,
                    target: ">=5%"
                };
            }),
            
            // Estructura básica pero completa
            segments_breakdown: [
                { segment: "Grupo A", kpis: [{ name: metricNames[0] || "CTR", value: 6.5, unit: "%" }] },
                { segment: "Grupo B", kpis: [{ name: metricNames[0] || "CTR", value: 4.2, unit: "%" }] }
            ],
            
            feedback_cualitativo: [
                { quote: "Funciona básicamente", sentiment: "neutral" },
                { quote: "Podría mejorarse", sentiment: "negative" }
            ],
            
            analysis: {
                descriptive: "Experimento básico completado con simulación local",
                diagnostic: "Funcionalidad limitada - conexión a IA no disponible", 
                recommendation: "Revisar configuración de servicios externos y reintentar"
            },
            
            veredicto: "revisar",
            
            // Para compatibilidad con UI existente
            insights: [
                "Simulación básica completada",
                "Datos generados localmente",
                "Recomendado: verificar conectividad con servicios IA"
            ]
        };
    };

    // 🎮 Simular proceso de experimento
    const startSimulation = async () => {
        setIsGenerating(true);
        setSimulationStep('running');

        try {
            // Simular tiempo de "ejecución" del experimento
            await new Promise(resolve => setTimeout(resolve, 3000));

            // Extraer métricas de measureData
            const metrics = measureData?.metricas_clave || measureData?.kpis_principales || [];
            console.log('[🚀 SIMULATION] Métricas extraídas:', metrics);
            
            // 🔍 DEBUG: Estado justo antes de llamar generateSimulatedResults
            console.log('[🔍 PRE-GENERATE] measureData antes de generar:', measureData);
            console.log('[🔍 PRE-GENERATE] metrics disponibles:', metrics);

            const results = await generateSimulatedResults();
            console.log('[🚀 SIMULATION] Resultados generados:', results);
            
            // Verificar que results no es null
            if (results) {
                setSimulatedData(results);
                setSimulationStep('completed');
                // Pasar datos al componente padre
                onSimulationComplete?.(results);
            } else {
                throw new Error('No se pudieron generar resultados válidos');
            }
            
        } catch (error) {
            console.error('[🚀 SIMULATION] Error en startSimulation:', error);
            
            // Fallback con datos mínimos pero funcionales
            const fallbackResults = {
                universe: { n_users: 50, period_days: experimentDuration || 7 },
                kpis: [
                    { name: "CTR", value: 5.2, unit: "%", confidence: 0.8, trend: "up", benchmark: "4.1% industria" },
                    { name: "Satisfacción", value: 3.4, unit: "/5", confidence: 0.75, trend: "down", benchmark: "3.8/5 industria" }
                ],
                analysis: {
                    descriptive: "Error en simulación - usando datos de respaldo para continuar",
                    diagnostic: "Sistema no pudo conectar con servicios de análisis avanzado",
                    recommendation: "Revisar conectividad y configuración, luego reintentar simulación"
                },
                veredicto: "revisar",
                feedback_cualitativo: [
                    { quote: "El sistema funcionó parcialmente", sentiment: "neutral" }
                ]
            };
            setSimulatedData(fallbackResults);
            setSimulationStep('completed');
            onSimulationComplete?.(fallbackResults);
        } finally {
            setIsGenerating(false);
        }
    };

    // 🔄 Regenerar datos
    const regenerateData = async () => {
        try {
            const newResults = await generateSimulatedResults();
            setSimulatedData(newResults);
            onSimulationComplete?.(newResults);
        } catch (error) {
            console.error('[🔄 REGENERATE] Error:', error);
        }
    };

    // 💾 Cargar datos existentes (SOLO si tienen estructura de simulación válida)
    useEffect(() => {
        if (initialData && typeof initialData === 'object' && initialData.kpis && Array.isArray(initialData.kpis)) {
            console.log('[💾 SIMULATE] Cargando datos de simulación previa:', initialData);
            setSimulatedData(initialData);
            setSimulationStep('completed');
        } else {
            console.log('[💾 SIMULATE] initialData no contiene simulación válida, esperando ejecución manual');
        }
    }, [initialData]);

    // 🔍 Debug measureData al montar el componente
    useEffect(() => {
        console.log('[🎲 BMLSimulatePhase] measureData recibida:', measureData);
        if (measureData) {
            console.log('[🎲 BMLSimulatePhase] Métricas clave:', measureData.metricas_clave);
            console.log('[🎲 BMLSimulatePhase] Métodos medición:', measureData.metodos_medicion);
            console.log('[🎲 BMLSimulatePhase] Objetivos:', measureData.objetivos_especificos);
        }
    }, [measureData]);

    if (!isActive) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg border border-blue-200 overflow-hidden">
            {/* 🎨 Header con gradiente SIMULATE */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold">SIMULATE: Ejecutar experimento</h3>
                        <p className="text-blue-100">Generamos datos realistas de tu experimento para analizar</p>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {simulationStep === 'setup' && (
                    <div className="space-y-6">
                        {/* Resumen del experimento */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                                <Target className="w-4 h-4" />
                                Experimento a simular:
                            </h4>
                            {measureData && (
                                <div className="space-y-2 text-sm text-blue-700">
                                    <p><strong>Hipótesis:</strong> {measureData.hipotesis_validar || measureData.hypothesis || 'No definida'}</p>
                                    <p><strong>Métricas a simular:</strong></p>
                                    {(() => {
                                        const metrics = measureData.metricas_clave || measureData.kpis_principales || [];
                                        return metrics.length > 0 ? (
                                            <ul className="list-disc list-inside ml-4">
                                                {metrics.map((kpi, i) => 
                                                    kpi && kpi.toString().trim() && <li key={i}>{kpi}</li>
                                                )}
                                            </ul>
                                        ) : (
                                            <p className="text-red-600 text-xs">⚠️ No hay métricas definidas en la fase MEASURE</p>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>

                        {/* Configuración */}
                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Duración del experimento simulado
                            </label>
                            <div className="flex items-center gap-4">
                                <input
                                    type="range"
                                    min="3"
                                    max="30"
                                    value={experimentDuration}
                                    onChange={(e) => setExperimentDuration(Number(e.target.value))}
                                    className="flex-1"
                                />
                                <span className="text-sm text-gray-600 min-w-[60px]">
                                    {experimentDuration} días
                                </span>
                            </div>
                        </div>

                        {/* Debug info */}
                        {process.env.NODE_ENV === 'development' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                                <div className="text-xs text-yellow-800">
                                    <strong>Debug:</strong> measureData = {measureData ? 'EXISTS' : 'NULL'}
                                    {measureData && (
                                        <div>
                                            <div>metricas_clave: {JSON.stringify(measureData.metricas_clave)}</div>
                                            <div>kpis_principales: {JSON.stringify(measureData.kpis_principales)}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Botón iniciar */}
                        <div className="flex justify-center pt-4">
                            <button
                                onClick={startSimulation}
                                disabled={!measureData || !(measureData.metricas_clave?.some(kpi => kpi && kpi.toString().trim().length > 0) || measureData.kpis_principales?.some(kpi => kpi && kpi.toString().trim().length > 0))}
                                className={`px-8 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                                    measureData && (measureData.metricas_clave?.some(kpi => kpi && kpi.toString().trim().length > 0) || measureData.kpis_principales?.some(kpi => kpi && kpi.toString().trim().length > 0))
                                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                <Play className="w-5 h-5" />
                                {measureData ? 'Ejecutar Experimento Simulado' : 'Completa primero la fase MEASURE'}
                            </button>
                        </div>
                    </div>
                )}

                {simulationStep === 'running' && (
                    <div className="text-center py-12">
                        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-800 mb-2">Ejecutando experimento...</h4>
                        <p className="text-gray-600">Recopilando datos de {experimentDuration} días de pruebas</p>
                        <div className="mt-4 flex justify-center items-center gap-2 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            Esto puede tardar unos segundos
                        </div>
                    </div>
                )}

                {simulationStep === 'completed' && simulatedData && (
                    <div className="space-y-6">
                        {/* Hipótesis evaluada (NUEVO v2.3) */}
                        {simulatedData.hypothesis_text && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                                    🎯 Hipótesis evaluada
                                </h4>
                                <p className="text-sm text-blue-700 italic">
                                    "{simulatedData.hypothesis_text}"
                                </p>
                            </div>
                        )}

                        {/* Status con veredicto mejorado v2.3 */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div>
                                    <h4 className="font-medium text-green-800">Experimento completado</h4>
                                    <p className="text-sm text-green-700">
                                        {simulatedData.universe?.n_users || simulatedData.total_participants || 0} participantes · 
                                        {simulatedData.universe?.period_days || simulatedData.duration_days || 0} días
                                    </p>
                                </div>
                            </div>
                            
                            {/* Veredicto prominente */}
                            {simulatedData.veredicto && (
                                <div className={`px-4 py-2 rounded-lg font-medium text-sm ${
                                    simulatedData.veredicto === 'aprobado' ? 'bg-green-100 text-green-800' :
                                    'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {simulatedData.veredicto === 'aprobado' ? '✅ Aprobado' : '⚠️ Revisar'}
                                </div>
                            )}
                        </div>

                        {/* Resultados KPIs - Compatible con estructura avanzada */}
                        <div>
                            <h4 className="font-medium text-gray-800 mb-4 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Resultados por KPI:
                            </h4>
                            {/* 📊 TABLA DE KPIS - Formato profesional */}
                            {!simulatedData.kpis && !simulatedData.kpi_results ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-yellow-800 text-sm">
                                        ⚠️ No se encontraron KPIs en los datos de simulación. 
                                        <br />
                                        Estructura recibida: {JSON.stringify(Object.keys(simulatedData))}
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                    📊 KPI
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                    🎯 Resultado
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                    📈 Benchmark
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                    📉 Desviación
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                    ✅ Confianza
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                    🔄 Estado
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {simulatedData.kpis && simulatedData.kpis.map((kpi, index) => {
                                                const interpretacion = simulatedData.interpretacion_kpi?.find(i => i.name === kpi.name);
                                                const confidenceInfo = interpretConfidence(kpi.confidence);
                                                
                                                return (
                                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                        {/* KPI Name con tooltip */}
                                                        <td className="px-4 py-4">
                                                            <div className="flex items-center gap-2">
                                                                <div className="font-medium text-gray-900 text-sm">
                                                                    {kpi.name}
                                                                </div>
                                                                <div className="group relative">
                                                                    <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center cursor-help">
                                                                        ?
                                                                    </div>
                                                                    <div className="absolute bottom-6 left-0 bg-gray-800 text-white text-xs rounded-lg p-3 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                                                        <div className="font-medium text-blue-300 mb-1">{kpi.name}</div>
                                                                        <div className="text-gray-300">
                                                                            {interpretacion?.explicacion || `Este KPI mide ${kpi.name} y es fundamental para evaluar el modelo de negocio.`}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Resultado */}
                                                        <td className="px-4 py-4 text-center">
                                                            <div className="text-xl font-bold text-gray-900">
                                                                {kpi.value}{kpi.unit}
                                                            </div>
                                                        </td>

                                                        {/* Benchmark Industria */}
                                                        <td className="px-4 py-4 text-center">
                                                            {kpi.industry_benchmark ? (
                                                                <div className="text-sm">
                                                                    <div className="font-medium text-blue-600">
                                                                        {kpi.industry_benchmark}{kpi.unit}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        Estándar
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 text-sm">N/A</span>
                                                            )}
                                                        </td>

                                                        {/* Desviación */}
                                                        <td className="px-4 py-4 text-center">
                                                            {kpi.deviation !== undefined ? (
                                                                <div className={`flex items-center justify-center gap-1 ${
                                                                    kpi.deviation > 0 ? 'text-green-600' : 'text-red-600'
                                                                }`}>
                                                                    <span className="text-lg">
                                                                        {kpi.deviation > 0 ? '↗️' : '↘️'}
                                                                    </span>
                                                                    <span className="font-medium">
                                                                        {kpi.deviation > 0 ? '+' : ''}{kpi.deviation}%
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 text-sm">N/A</span>
                                                            )}
                                                        </td>

                                                        {/* Confianza */}
                                                        <td className="px-4 py-4 text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${confidenceInfo.bgColor} ${confidenceInfo.textColor}`}>
                                                                    {confidenceInfo.icon} {Math.round(kpi.confidence * 100)}%
                                                                </span>
                                                                <div className="group relative">
                                                                    <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center cursor-help">
                                                                        ℹ️
                                                                    </div>
                                                                    <div className="absolute bottom-6 right-0 bg-gray-800 text-white text-xs rounded-lg p-3 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                                                                        <div className="font-medium mb-1">🧠 Interpretación IA:</div>
                                                                        <div className="mb-2">{confidenceInfo.explicacion}</div>
                                                                        <div className="text-gray-300">
                                                                            <strong>Sugerencia:</strong> {confidenceInfo.accion}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Estado/Tendencia */}
                                                        <td className="px-4 py-4 text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <span className="text-lg">
                                                                    {interpretacion?.tendencia === 'positiva' ? '🔼' :
                                                                     interpretacion?.tendencia === 'negativa' ? '🔽' : '⚪'}
                                                                </span>
                                                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                                                    interpretacion?.etiqueta === 'validada' ? 'bg-green-100 text-green-700' :
                                                                    interpretacion?.etiqueta === 'revisar' ? 'bg-red-100 text-red-700' :
                                                                    'bg-yellow-100 text-yellow-700'
                                                                }`}>
                                                                    {interpretacion?.etiqueta === 'validada' ? 'Validada' :
                                                                     interpretacion?.etiqueta === 'revisar' ? 'Revisar' :
                                                                     'Ambigua'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {/* Estructura legacy: simulatedData.kpi_results object */}
                                            {simulatedData.kpi_results && Object.entries(simulatedData.kpi_results).map(([kpiName, data], index) => (
                                                <tr key={`legacy-${index}`} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-4">
                                                        <div className="font-medium text-gray-900 text-sm">{kpiName}</div>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <div className="text-xl font-bold text-gray-900">
                                                            {data.value}{data.unit}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className="text-gray-400 text-sm">N/A</span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className={`text-lg ${
                                                            data.trend === 'up' ? 'text-green-600' :
                                                            data.trend === 'down' ? 'text-red-600' : 'text-gray-400'
                                                        }`}>
                                                            {data.trend === 'up' ? '↗️' : data.trend === 'down' ? '↘️' : '→'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                                            {data.confidence}%
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className="text-gray-400 text-sm">Legacy</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Resumen de confianza del experimento (NUEVO) */}
                        {simulatedData.kpis && simulatedData.kpis.length > 0 && (
                            <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-lg p-4">
                                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                                    📊 Resumen de fiabilidad experimental
                                </h4>
                                {(() => {
                                    // Calcular confianza promedio
                                    const avgConfidence = simulatedData.kpis.reduce((sum, kpi) => sum + kpi.confidence, 0) / simulatedData.kpis.length;
                                    const confidenceInfo = interpretConfidence(avgConfidence);
                                    
                                    return (
                                        <div className={`p-3 rounded-lg border-l-4 ${
                                            confidenceInfo.nivel === 'alta' ? 'bg-green-50 border-green-400' :
                                            confidenceInfo.nivel === 'media' ? 'bg-yellow-50 border-yellow-400' :
                                            'bg-red-50 border-red-400'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-lg">{confidenceInfo.icon}</span>
                                                <span className={`font-medium ${confidenceInfo.textColor}`}>
                                                    Confianza general: {Math.round(avgConfidence * 100)}% ({confidenceInfo.nivel})
                                                </span>
                                            </div>
                                            <p className={`text-sm ${confidenceInfo.textColor} mb-2`}>
                                                {confidenceInfo.descripcion}
                                            </p>
                                            <p className={`text-xs ${confidenceInfo.textColor} font-medium`}>
                                                💡 Recomendación: {confidenceInfo.accion}
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Desglose por segmentos - NUEVO según consolidación */}
                        {simulatedData.segments_breakdown && simulatedData.segments_breakdown.length > 0 && (
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
                                <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Análisis por segmentos
                                </h4>
                                
                                <div className="space-y-4">
                                    {simulatedData.segments_breakdown.map((segmentData, index) => (
                                        <div key={index} className="bg-white bg-opacity-60 rounded-lg p-4">
                                            <h5 className="font-medium text-purple-800 mb-3">
                                                📊 {segmentData.segment}
                                            </h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {segmentData.kpis.map((kpi, kpiIndex) => (
                                                    <div key={kpiIndex} className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                                                        <div className="text-sm font-medium text-purple-900">{kpi.name}</div>
                                                        <div className="text-xl font-bold text-purple-700">
                                                            {kpi.value}{kpi.unit}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Análisis inteligente v2.3 */}
                        {simulatedData.analysis && (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                                <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                                    <Brain className="w-5 h-5" />
                                    Análisis inteligente del experimento
                                </h4>
                                
                                <div className="space-y-4">
                                    {/* Descriptivo */}
                                    <div className="bg-white bg-opacity-60 rounded-lg p-4">
                                        <h5 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                            📊 Análisis descriptivo
                                        </h5>
                                        <p className="text-sm text-gray-700">{simulatedData.analysis.descriptive}</p>
                                    </div>
                                    
                                    {/* Diagnóstico */}
                                    <div className="bg-white bg-opacity-60 rounded-lg p-4">
                                        <h5 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                            🔍 Análisis diagnóstico
                                        </h5>
                                        <p className="text-sm text-gray-700">{simulatedData.analysis.diagnostic}</p>
                                    </div>
                                    
                                    {/* Recomendación */}
                                    <div className="bg-white bg-opacity-60 rounded-lg p-4">
                                        <h5 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                            💡 Recomendación estratégica
                                        </h5>
                                        <p className="text-sm text-gray-700">{simulatedData.analysis.recommendation}</p>
                                    </div>
                                    
                                    {/* Resumen analítico (NUEVO v2.3) */}
                                    {simulatedData.resumen_analitico && (
                                        <div className="bg-indigo-100 rounded-lg p-4 border-l-4 border-indigo-500">
                                            <h5 className="font-medium text-indigo-800 mb-2 flex items-center gap-2">
                                                📋 Resumen ejecutivo
                                            </h5>
                                            <p className="text-sm text-indigo-700 font-medium">{simulatedData.resumen_analitico}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Summary Score - NUEVO según consolidación */}
                        {simulatedData.summary_score && (
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                                <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                                    <Target className="w-5 h-5" />
                                    Evaluación del experimento
                                </h4>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl mb-2">
                                            {simulatedData.summary_score.impact === 'alto' ? '🚀' : 
                                             simulatedData.summary_score.impact === 'medio' ? '📈' : '📊'}
                                        </div>
                                        <div className="font-medium text-green-800">Impacto</div>
                                        <div className={`text-sm font-semibold ${
                                            simulatedData.summary_score.impact === 'alto' ? 'text-green-700' :
                                            simulatedData.summary_score.impact === 'medio' ? 'text-yellow-700' : 'text-red-700'
                                        }`}>
                                            {simulatedData.summary_score.impact.charAt(0).toUpperCase() + simulatedData.summary_score.impact.slice(1)}
                                        </div>
                                    </div>
                                    
                                    <div className="text-center">
                                        <div className="text-2xl mb-2">
                                            {simulatedData.summary_score.clarity === 'alto' ? '🔍' : 
                                             simulatedData.summary_score.clarity === 'medio' ? '👁️' : '❓'}
                                        </div>
                                        <div className="font-medium text-green-800">Claridad</div>
                                        <div className={`text-sm font-semibold ${
                                            simulatedData.summary_score.clarity === 'alto' ? 'text-green-700' :
                                            simulatedData.summary_score.clarity === 'medio' ? 'text-yellow-700' : 'text-red-700'
                                        }`}>
                                            {simulatedData.summary_score.clarity.charAt(0).toUpperCase() + simulatedData.summary_score.clarity.slice(1)}
                                        </div>
                                    </div>
                                    
                                    <div className="text-center">
                                        <div className="text-2xl mb-2">
                                            {simulatedData.summary_score.retention === 'alto' ? '🔄' : 
                                             simulatedData.summary_score.retention === 'medio' ? '↩️' : '⏰'}
                                        </div>
                                        <div className="font-medium text-green-800">Retención</div>
                                        <div className={`text-sm font-semibold ${
                                            simulatedData.summary_score.retention === 'alto' ? 'text-green-700' :
                                            simulatedData.summary_score.retention === 'medio' ? 'text-yellow-700' : 'text-red-700'
                                        }`}>
                                            {simulatedData.summary_score.retention.charAt(0).toUpperCase() + simulatedData.summary_score.retention.slice(1)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Feedback cualitativo v2.3 */}
                        {(simulatedData.qual_feedback || simulatedData.feedback_cualitativo) && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h4 className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
                                    💬 Feedback de usuarios (3-5 citas)
                                </h4>
                                <div className="space-y-3">
                                    {/* Formato v2.3 con "tone" */}
                                    {simulatedData.qual_feedback && simulatedData.qual_feedback.map((feedback, index) => (
                                        <div key={index} className="bg-white bg-opacity-60 rounded-lg p-3 border-l-4 border-gray-300">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-700 italic mb-2">"{feedback.quote}"</p>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-500">
                                                            {feedback.segment}
                                                        </span>
                                                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                                                            feedback.tone === 'positive' ? 'bg-green-100 text-green-700' :
                                                            feedback.tone === 'negative' ? 'bg-red-100 text-red-700' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>
                                                            {feedback.tone === 'positive' ? '😊 Positivo' :
                                                             feedback.tone === 'negative' ? '😐 Negativo' :
                                                             '😐 Neutral'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Fallback para formato anterior */}
                                    {!simulatedData.qual_feedback && simulatedData.feedback_cualitativo && simulatedData.feedback_cualitativo.map((feedback, index) => (
                                        <div key={index} className="bg-white bg-opacity-60 rounded p-3">
                                            <p className="text-sm text-gray-700 italic">"{feedback.quote}"</p>
                                            <span className={`text-xs mt-1 inline-block px-2 py-1 rounded ${
                                                feedback.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                                                feedback.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                                                'bg-gray-100 text-gray-700'
                                            }`}>
                                                {feedback.sentiment}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Insights automáticos (Fallback para estructura legacy) */}
                        {simulatedData.insights && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Insights preliminares:
                                </h4>
                                <ul className="space-y-2">
                                    {simulatedData.insights.map((insight, index) => (
                                        <li key={index} className="flex items-start gap-2 text-sm text-purple-700">
                                            <span className="text-purple-500 mt-0.5">•</span>
                                            {insight}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Controles v2.3 */}
                        <div className="flex flex-wrap gap-3 pt-4">
                            <button
                                onClick={regenerateData}
                                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Regenerar datos
                            </button>
                            
                            {/* Editar referencia (NUEVO v2.3) */}
                            {simulatedData.comparativo?.editable_por_usuario && (
                                <button
                                    onClick={() => alert('Modal de edición de benchmarks - Por implementar')}
                                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    ⚙️ Editar referencia
                                </button>
                            )}
                            
                            <button
                                onClick={() => onSimulationComplete?.(simulatedData, 'continue')}
                                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all"
                            >
                                Continuar a LEARN
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BMLSimulateStep;